import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { initialState } from "./missionsTypes";

export const Slice = createSlice({
  name: "missions",
  initialState,
  reducers: {
    // Master Mission Reducers
    setMasters: (state, action: PayloadAction<MasterMission[]>) => {
      state.masters.byId = {};
      state.masters.allIds = [];

      action.payload.forEach((master) => {
        state.masters.byId[master.id] = master;
        state.masters.allIds.push(master.id);
      });
    },

    addMaster: (state, action: PayloadAction<MasterMission>) => {
      const master = action.payload;
      if (!state.masters.byId[master.id]) {
        state.masters.byId[master.id] = master;
        state.masters.allIds.push(master.id);
      }
    },

    updateMaster: (state, action: PayloadAction<MasterMission>) => {
      const master = action.payload;
      if (state.masters.byId[master.id]) {
        state.masters.byId[master.id] = master;
      }
    },

    removeMaster: (state, action: PayloadAction<string>) => {
      const masterId = action.payload;
      delete state.masters.byId[masterId];
      state.masters.allIds = state.masters.allIds.filter(
        (id) => id !== masterId
      );

      // Clean up associated versions
      delete state.versions.byMasterId[masterId];
      delete state.versions.loading[masterId];

      // Clear selection if removed master was selected
      if (state.selectedMasterId === masterId) {
        state.selectedMasterId = null;
        state.selectedVersionId = null;
      }
    },

    // Version Reducers
    setVersions: (
      state,
      action: PayloadAction<{ masterId: string; versions: Version[] }>
    ) => {
      const { masterId, versions } = action.payload;
      state.versions.byMasterId[masterId] = versions;
      state.versions.loading[masterId] = false;
    },

    addVersion: (
      state,
      action: PayloadAction<{ masterId: string; version: Version }>
    ) => {
      const { masterId, version } = action.payload;
      if (!state.versions.byMasterId[masterId]) {
        state.versions.byMasterId[masterId] = [];
      }
      state.versions.byMasterId[masterId].push(version);
    },

    updateVersion: (
      state,
      action: PayloadAction<{ masterId: string; version: Version }>
    ) => {
      const { masterId, version } = action.payload;
      const versions = state.versions.byMasterId[masterId];
      if (versions) {
        const index = versions.findIndex((v) => v.id === version.id);
        if (index >= 0) {
          versions[index] = version;
        }
      }
    },

    removeVersion: (
      state,
      action: PayloadAction<{ masterId: string; versionId: string }>
    ) => {
      const { masterId, versionId } = action.payload;
      const versions = state.versions.byMasterId[masterId];
      if (versions) {
        state.versions.byMasterId[masterId] = versions.filter(
          (v) => v.id !== versionId
        );
      }

      // Clear selection if removed version was selected
      if (state.selectedVersionId === versionId) {
        state.selectedVersionId = null;
      }
    },

    // Selection Reducers
    selectMaster: (state, action: PayloadAction<string | null>) => {
      state.selectedMasterId = action.payload;
      // Reset version selection when master changes
      state.selectedVersionId = null;
    },

    selectVersion: (state, action: PayloadAction<string | null>) => {
      state.selectedVersionId = action.payload;
    },

    clearSelection: (state) => {
      state.selectedMasterId = null;
      state.selectedVersionId = null;
    },

    // Loading state for versions
    setVersionsLoading: (
      state,
      action: PayloadAction<{ masterId: string; loading: boolean }>
    ) => {
      const { masterId, loading } = action.payload;
      state.versions.loading[masterId] = loading;
    },

    // Reset
    resetMissions: () => initialState,

    // Error handling
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },

    // Global loading state
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
  },
});

export default Slice;
